import { useState } from 'react';
import loadable from '@loadable/component';

import CrossSolid from '@simplywallst/ui-core/icons/CrossSolid';

import If from '@/components/If';
import type { ActiveModal } from '@/components/Nav/DesktopTopbar/components/DesktopTopbarAccountAccess';

import useUser from '@/hooks/useUser';
import { useUserAccess } from '@/hooks/useUserAccess';

import {
  ROUTE_WELCOME,
  ROUTE_USER_LOGOUT,
  ROUTE_USER_REGISTER,
  ROUTE_PRO_API,
  ROUTE_USER_BETA_LAB,
  ROUTE_USER_NOTIFICATIONS,
  ROUTE_USER_SUBSCRIPTION,
  ROUTE_USER_SUBSCRIPTION_AND_BILLING,
  ROUTE_PLANS,
  ROUTE_USER_PROFILE,
} from '@/constants/routes';

import {
  List,
  Header,
  ListLabel,
  BackButton,
  ScrollPane,
  Wrapper,
} from './styled';
import MenuItem from '../MenuItem';
import { useProApiAccess } from '@/pages/ProApi/hooks/useProApiAccess';
import { useTranslation } from '@/hooks/useTranslation';
import { SWS_HOST, SWS_SUPPORT, SWS_TERMS_AND_CONDITIONS } from '@/constants';
import { useLocale } from '@/hooks/useLocale';
import { useFlag } from '@/features/unleash';
import { isPageTranslated } from '@/utils/locale';
import { useLocation } from '@/hooks/useLocation';

const ViewedCompaniesModal = loadable(
  () =>
    import(
      '@/components/User/components/ViewedCompaniesModal/ViewedCompaniesModal'
    )
);

const AppVersion = loadable(() => import('./components/AppVersion'), {
  resolveComponent: (component) => component.AppVersion,
});

const LocaleModal = loadable(() => import('./components/LocaleModal'), {
  resolveComponent: (c) => c.LocaleModal,
});

interface Props {
  onClose: () => void;
}

const MenuOverlay = ({ onClose }: Props) => {
  const [activeModal, setActiveModal] = useState<ActiveModal>('none');
  const { data: userData } = useUser();
  const { data: userAccess } = useUserAccess();
  const isTrialUser = userData.billing.status === 'trialing';

  const { canCreateApiKeys } = useProApiAccess();
  const t = useTranslation();

  const locale = useLocale();
  const languageDropdownFlag = useFlag({ name: 'language-dropdown' });
  const locationState = useLocation();
  const showLanguageDropdown =
    languageDropdownFlag.enabled && isPageTranslated(locationState.type);

  const [showLocaleModal, setShowLocaleModal] = useState(false);
  const handleLocaleClick = (
    e:
      | React.MouseEvent<HTMLAnchorElement, MouseEvent>
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
      | MouseEvent
      | TouchEvent
  ) => {
    e.preventDefault();
    setShowLocaleModal(true);
  };

  return (
    <Wrapper>
      <Header>
        <BackButton onClick={onClose}>
          <CrossSolid />
        </BackButton>
      </Header>
      <ScrollPane>
        <List>
          <ListLabel>
            <span>{t('app.topbar.nav.mobile.account.myAccount')}</span>
          </ListLabel>
          {userData.loggedIn ? (
            <>
              {userAccess.planType !== '' && (
                <MenuItem
                  label={t('app.topbar.nav.account.profile')}
                  to={ROUTE_USER_PROFILE}
                  onClick={onClose}
                />
              )}

              {userAccess.provider === 'stripe' && (
                <>
                  {/* Stripe subscriptions can manage and upgrade their accounts via Stripe Portal and Plan pages */}
                  {RUNTIME_ENV !== 'native' && !isTrialUser && (
                    <MenuItem
                      label={t('app.topbar.nav.account.subscription')}
                      to={ROUTE_USER_SUBSCRIPTION_AND_BILLING}
                      onClick={onClose}
                    />
                  )}

                  {userAccess.planType !== '' && (
                    <MenuItem
                      label={
                        RUNTIME_ENV === 'native'
                          ? t('app.topbar.nav.mobile.account.myPlan')
                          : t('app.topbar.nav.account.plans')
                      }
                      to={ROUTE_PLANS}
                      onClick={onClose}
                    />
                  )}

                  {canCreateApiKeys && (
                    <MenuItem
                      label={t('app.topbar.nav.account.proAPI')}
                      to={ROUTE_PRO_API}
                      onClick={onClose}
                    />
                  )}

                  {userAccess.planType !== '' &&
                    userAccess.planType !== 'unlimited' && (
                      <MenuItem
                        label={t('app.topbar.nav.account.viewedCompanies')}
                        to={' '}
                        onClick={() => setActiveModal('viewedCompanies')}
                      />
                    )}

                  <MenuItem
                    label={t('app.topbar.nav.account.notifications')}
                    to={ROUTE_USER_NOTIFICATIONS}
                    onClick={onClose}
                  />
                </>
              )}

              {userAccess.provider !== 'stripe' && (
                <>
                  {/* Non-Stripe paying customers (e.g Apple IAP) will manage their subscriptions externally */}
                  {RUNTIME_ENV !== 'native' && (
                    <MenuItem
                      label={t('app.topbar.nav.account.manageSubscription')}
                      to={ROUTE_USER_SUBSCRIPTION}
                      onClick={onClose}
                    />
                  )}

                  {canCreateApiKeys && (
                    <MenuItem
                      label={t('app.topbar.nav.account.proAPI')}
                      to={ROUTE_PRO_API}
                      onClick={onClose}
                    />
                  )}

                  {userAccess.planType !== 'unlimited' && (
                    <MenuItem
                      label={t('app.topbar.nav.account.viewedCompanies')}
                      to={' '}
                      onClick={() => setActiveModal('viewedCompanies')}
                    />
                  )}

                  <MenuItem
                    label={t('app.topbar.nav.account.notifications')}
                    to={ROUTE_USER_NOTIFICATIONS}
                    onClick={onClose}
                  />
                </>
              )}

              {RUNTIME_ENV !== 'native' && (
                <MenuItem
                  label={t('app.topbar.nav.account.betaLab')}
                  to={ROUTE_USER_BETA_LAB}
                  onClick={onClose}
                />
              )}

              {showLanguageDropdown && (
                <MenuItem
                  label={t('app.topbar.nav.account.localeSelector', {
                    locale: locale.toUpperCase(),
                  })}
                  to={'/'}
                  onClick={handleLocaleClick}
                />
              )}

              <MenuItem
                label={t('app.topbar.nav.account.logout')}
                to={ROUTE_USER_LOGOUT}
                onClick={onClose}
              />
            </>
          ) : (
            <>
              <MenuItem
                label={t('app.topbar.nav.mobile.account.login')}
                to={ROUTE_WELCOME}
                onClick={onClose}
              />
              <MenuItem
                label={t('app.topbar.nav.mobile.account.signup')}
                to={ROUTE_USER_REGISTER}
                onClick={onClose}
              />
              {showLanguageDropdown && (
                <MenuItem
                  label={t('app.topbar.nav.account.localeSelector', {
                    locale: locale.toUpperCase(),
                  })}
                  to={'/'}
                  onClick={handleLocaleClick}
                />
              )}
            </>
          )}
          <ListLabel>
            <span>{t('app.topbar.nav.mobile.account.otherUsefulLinks')}</span>
          </ListLabel>
          <MenuItem
            label={t('app.topbar.nav.mobile.account.help')}
            href={SWS_SUPPORT}
            onClick={onClose}
            target="_blank"
            rel="noopener noreferrer"
          />
          <MenuItem
            label={t('app.topbar.nav.mobile.account.about')}
            href={SWS_HOST + '/about'}
            onClick={onClose}
          />
          <MenuItem
            label={t('app.topbar.nav.mobile.account.termsAndConditions')}
            href={SWS_TERMS_AND_CONDITIONS}
            onClick={onClose}
          />
          <MenuItem
            label={t('app.topbar.nav.mobile.account.privacyPolicy')}
            href={SWS_HOST + '/privacypolicy'}
            onClick={onClose}
          />
          {RUNTIME_ENV === 'native' ? <AppVersion /> : null}
        </List>
      </ScrollPane>
      <If
        conditions={{
          viewedCompanies: activeModal === 'viewedCompanies',
        }}
      >
        {{
          viewedCompanies: () => <ViewedCompaniesModal onClose={onClose} />,
        }}
      </If>
      {showLocaleModal && (
        <LocaleModal
          onConfirm={() => {
            setShowLocaleModal(false);
            onClose();
          }}
          onClose={() => setShowLocaleModal(false)}
        />
      )}
    </Wrapper>
  );
};

export default MenuOverlay;
